import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import useUrlState from "@/hooks/useUrlState";
import { isAutoRefund } from "@/lib/refundReason";
import RefundReasonBadge from "@/components/feature/RefundReasonBadge";

interface Tx {
  id: string;
  user_id: string;
  type: string | null;
  amount: number;
  balance_after: number;
  description: string | null;
  refund_reason: string | null;
  payment_method: string | null;
  status: string | null;
  admin_notes: string | null;
  created_at: string;
  profiles?: {
    email?: string | null;
    full_name?: string | null;
  } | null;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 탭 복귀(리로드) 시 로딩 없이 마지막 데이터를 즉시 보여주기 위한 캐시
const TX_CACHE_KEY = "cache:admin_credit_transactions";

function readTxCache(): Tx[] | null {
  try {
    const raw = sessionStorage.getItem(TX_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Tx[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeTxCache(list: Tx[]): void {
  try {
    sessionStorage.setItem(TX_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

const TYPE_OPTIONS = ["전체", "charge", "use", "bonus", "refund"];
const STATUS_OPTIONS = ["전체", "pending", "approved", "rejected"];

const TX_LABELS: Record<string, string> = {
  charge: "충전",
  use: "사용",
  bonus: "보너스",
  refund: "환불",
};

const TYPE_LABELS_WITH_STATUS: Record<string, string> = {
  charge_approved: "충전 완료",
  charge_pending: "승인 대기",
  charge_rejected: "충전 거절",
  use: "사용 완료",
  bonus: "보너스 지급",
  admin_grant: "관리자 지급",
  admin_deduct: "관리자 차감",
  refund: "환불 완료",
};

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: "승인 대기", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  approved: { label: "승인 완료", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  rejected: { label: "거절", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
};

function getStatusDisplay(type: string | null, status: string | null): { label: string; color: string; bg: string } | null {
  const t = (type || "").toLowerCase();
  const s = (status || "").toLowerCase();

  if (t === "charge") {
    return STATUS_LABELS[s] || null;
  }

  const txKey = TYPE_LABELS_WITH_STATUS[t];
  if (txKey) {
    return { label: txKey, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" };
  }

  return null;
}

const PM_LABELS: Record<string, string> = {
  card: "카드",
  kakao: "카카오페이",
  naver: "네이버페이",
  bank: "무통장",
  "": "관리자 조정",
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function getPageItems(current: number, total: number): (number | "...")[] {
  const pages = new Set<number>();
  pages.add(1);
  pages.add(total);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items: (number | "...")[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) items.push("...");
    items.push(p);
    prev = p;
  }
  return items;
}

export default function CreditHistoryTab() {
  const { session } = useAuth();
  const cachedItemsRef = useRef<Tx[] | null>(readTxCache());
  const [items, setItems] = useState<Tx[]>(cachedItemsRef.current ?? []);
  const [loading, setLoading] = useState(cachedItemsRef.current === null);
  const [error, setError] = useState<string | null>(null);
  // 필터 상태를 주소(URL)에도 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [search, setSearch] = useUrlState("q", "");
  const [typeFilter, setTypeFilter] = useUrlState("type", "전체");
  const [statusFilter, setStatusFilter] = useUrlState("status", "전체");
  const [activeTabRaw, setActiveTabRaw] = useUrlState("tab", "all");
  const [pageRaw, setPageRaw] = useUrlState("page", "1");

  const activeTab: "all" | "pending" = activeTabRaw === "pending" ? "pending" : "all";
  const setActiveTab = (next: "all" | "pending") => setActiveTabRaw(next);

  const page = Math.max(1, parseInt(pageRaw, 10) || 1);
  const setPage = (next: number | ((prev: number) => number)) => {
    const target = typeof next === "function" ? next(page) : next;
    setPageRaw(String(Math.max(1, target)));
  };
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [modalTx, setModalTx] = useState<Tx | null>(null);
  const [modalAction, setModalAction] = useState<"approve" | "reject" | null>(null);
  const [modalNotes, setModalNotes] = useState("");
  const [modalError, setModalError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const PER_PAGE = 12;

  const fetchData = useCallback(async () => {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    // 캐시된 데이터가 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedItemsRef.current === null) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "전체") {
        params.set("type", typeFilter.toLowerCase());
      }
      const qs = params.toString();
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/get-admin-credit-transactions${qs ? `?${qs}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const json = await res.json();
      const list: Tx[] = json.transactions ?? [];
      setItems(list);
      writeTxCache(list);
    } catch (err: any) {
      console.error("[CreditHistory] fetch error:", err);
      setError(err.message || "충전 내역을 불러오는 중 오류가 발생했습니다.");
    } finally {
      cachedItemsRef.current = [];
      setLoading(false);
    }
  }, [session, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    let result = [...items];
    if (activeTab === "pending") {
      result = result.filter((t) => t.type === "charge" && t.status === "pending");
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (t) =>
          (t.profiles?.email?.toLowerCase() ?? "").includes(q) ||
          (t.profiles?.full_name?.toLowerCase() ?? "").includes(q) ||
          (t.description?.toLowerCase() ?? "").includes(q)
      );
    }
    if (typeFilter !== "전체") {
      result = result.filter((t) => (t.type || "").toLowerCase() === typeFilter.toLowerCase());
    }
    if (statusFilter !== "전체") {
      result = result.filter((t) => (t.status || "").toLowerCase() === statusFilter.toLowerCase());
    }
    return result;
  }, [items, search, typeFilter, statusFilter, activeTab]);

  // 필터가 실제로 바뀌면 첫 페이지로 (최초 복원 시에는 페이지 유지)
  const skipPageResetRef = useRef(true);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setPage(1);
  }, [search, typeFilter, statusFilter, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const pendingCount = items.filter((t) => t.type === "charge" && t.status === "pending").length;
  const totalIn = items.filter((t) => (t.amount || 0) > 0 && t.status === "approved").reduce((s, t) => s + t.amount, 0);
  const totalOut = items.filter((t) => (t.amount || 0) < 0).reduce((s, t) => s + t.amount, 0);

  const autoRefundItems = items.filter((t) => t.type === "refund" && isAutoRefund(t.refund_reason, t.description));
  const autoRefundCount = autoRefundItems.length;
  const autoRefundAmount = autoRefundItems.reduce((s, t) => s + Math.abs(t.amount || 0), 0);

  const handleApprove = async () => {
    if (!modalTx || !modalAction || !session) return;
    setProcessingId(modalTx.id);
    setModalError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-approve-charge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          transaction_id: modalTx.id,
          action: modalAction,
          admin_notes: modalNotes || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setModalError(data.error || "처리 중 오류가 발생했습니다");
        setProcessingId(null);
        return;
      }

      setModalTx(null);
      setModalAction(null);
      setModalNotes("");
      await fetchData();
    } catch (err) {
      setModalError("네트워크 오류가 발생했습니다");
    } finally {
      setProcessingId(null);
    }
  };

  const openModal = (tx: Tx, action: "approve" | "reject") => {
    setModalTx(tx);
    setModalAction(action);
    setModalNotes("");
    setModalError(null);
  };

  const closeModal = () => {
    setModalTx(null);
    setModalAction(null);
    setModalNotes("");
    setModalError(null);
  };

  // 현재 필터 상태가 담긴 주소(URL)를 클립보드에 복사
  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch {
        // 복사 실패는 무시
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">충전 내역 불러오는 중...</p>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <h2 className="text-white font-semibold text-lg">크레딧 관리</h2>
          <p className="text-gray-500 text-sm mt-0.5">총 {items.length.toLocaleString()}건의 거래 기록</p>
        </div>
        <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3">
          <div className="flex-1 text-center">
            <p className="text-emerald-400 text-sm font-bold">+₩{totalIn.toLocaleString()}</p>
            <p className="text-gray-600 text-[11px]">총 승인 충전</p>
          </div>
          <div className="w-px h-8 bg-white/[0.1]"></div>
          <div className="flex-1 text-center">
            <p className="text-rose-400 text-sm font-bold">₩{Math.abs(totalOut).toLocaleString()}</p>
            <p className="text-gray-600 text-[11px]">총 차감</p>
          </div>
          <div className="w-px h-8 bg-white/[0.1]"></div>
          <div className="flex-1 text-center">
            <p className="text-amber-400 text-sm font-bold">₩{autoRefundAmount.toLocaleString()}</p>
            <p className="text-gray-600 text-[11px]">자동 환불 {autoRefundCount}건</p>
          </div>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5 w-fit">
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
            activeTab === "all" ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
          }`}
        >
          전체 내역
        </button>
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all flex items-center gap-1.5 ${
            activeTab === "pending" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"
          }`}
        >
          승인 대기
          {pendingCount > 0 && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              activeTab === "pending" ? "bg-white/20 text-white" : "bg-amber-500/20 text-amber-400"
            }`}>
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="회원명, 이메일, 설명 검색"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-rose-500/40 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeTab === "all" && (
            <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
              {TYPE_OPTIONS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                    typeFilter === t ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                  }`}
                >
                  {TX_LABELS[t] || t}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  statusFilter === s ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {STATUS_LABELS[s]?.label || s}
              </button>
            ))}
          </div>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            title="현재 필터 상태의 주소를 복사합니다"
          >
            <i className={linkCopied ? "ri-check-line text-emerald-400" : "ri-link"}></i>
            {linkCopied ? "복사됨!" : "필터 상태 링크 복사"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/[0.08]">
                {["날짜", "회원", "유형", "금액", "상태", "방법", "잔액", "설명", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-gray-500 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-500 text-sm">
                    조건에 맞는 내역이 없습니다
                  </td>
                </tr>
              ) : (
                paged.map((t) => {
                  const isPositive = (t.amount || 0) > 0;
                  const userName = t.profiles?.full_name || t.profiles?.email || "알 수 없음";
                  const st = getStatusDisplay(t.type, t.status);
                  const isChargePending = t.type === "charge" && t.status === "pending";
                  return (
                    <tr key={t.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {formatDateTime(t.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white text-sm truncate max-w-[160px]">
                        {userName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isPositive ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"
                          }`}>
                            {TX_LABELS[t.type || ""] || t.type || "-"}
                          </span>
                          {t.type === "refund" && (
                            <RefundReasonBadge reason={t.refund_reason} description={t.description} />
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-bold ${isPositive ? "text-emerald-400" : "text-rose-400"}`}>
                        {isPositive ? "+" : ""}₩{(t.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {st ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${st.bg} ${st.color}`}>
                            {st.label}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {PM_LABELS[t.payment_method || ""] || t.payment_method || "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white text-sm">
                        ₩{(t.balance_after || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[160px]">
                        {t.description || "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isChargePending && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openModal(t, "approve")}
                              disabled={processingId === t.id}
                              className="px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold hover:bg-emerald-500/25 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              {processingId === t.id ? "처리중..." : "승인"}
                            </button>
                            <button
                              onClick={() => openModal(t, "reject")}
                              disabled={processingId === t.id}
                              className="px-2 py-1 rounded-md bg-red-500/15 text-red-400 text-[10px] font-semibold hover:bg-red-500/25 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              거절
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 py-3 border-t border-white/[0.08]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>
            {getPageItems(safePage, totalPages).map((p, idx) =>
              p === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 text-xs select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    p === safePage
                      ? "bg-rose-500/20 border border-rose-500/30 text-rose-400"
                      : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        )}
      </div>

      {/* Approve/Reject Modal */}
      {modalTx && modalAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative w-full max-w-sm bg-[#0f0f1e] border border-white/[0.1] rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-5 border-b border-white/[0.07]">
              <h3 className="text-white font-semibold text-base">
                {modalAction === "approve" ? "충전 승인" : "충전 거절"}
              </h3>
              <p className="text-gray-500 text-xs mt-0.5">
                {modalTx.profiles?.full_name || modalTx.profiles?.email || "회원"} 님의 ₩{(modalTx.amount || 0).toLocaleString()} 충전 요청
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">회원</span>
                  <span className="text-white">{modalTx.profiles?.full_name || modalTx.profiles?.email || "-"}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">금액</span>
                  <span className="text-emerald-400 font-semibold">+₩{(modalTx.amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">결제 수단</span>
                  <span className="text-white">{PM_LABELS[modalTx.payment_method || ""] || modalTx.payment_method || "-"}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">신청일</span>
                  <span className="text-white">{formatDateTime(modalTx.created_at)}</span>
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1.5 block">관리자 메모 (선택)</label>
                <textarea
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                  placeholder={modalAction === "approve" ? "선택사항" : "거절 사유를 입력해주세요 (선택)"}
                  maxLength={500}
                  className="w-full bg-white/[0.04] border border-white/[0.1] focus:border-rose-500/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-gray-600 resize-none"
                  rows={3}
                />
                <p className="text-gray-600 text-[10px] mt-1 text-right">{modalNotes.length}/500</p>
              </div>
              {modalError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <i className="ri-error-warning-line text-sm"></i>
                  {modalError}
                </div>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 py-3 rounded-xl border border-white/[0.1] text-gray-400 hover:text-white hover:border-white/[0.2] text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleApprove}
                disabled={processingId === modalTx.id}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 ${
                  modalAction === "approve"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white cursor-pointer hover:scale-[1.02]"
                    : "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white cursor-pointer hover:scale-[1.02]"
                } ${processingId === modalTx.id ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {modalAction === "approve" ? (
                  <>
                    <i className="ri-check-line text-base"></i>
                    승인하기
                  </>
                ) : (
                  <>
                    <i className="ri-close-line text-base"></i>
                    거절하기
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}